<script setup>
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { ref, watch, onMounted } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import CardBox from '@/Components/CardBox.vue';
import FormField from '@/Components/FormField.vue';
import FormControl from '@/Components/FormControl.vue';
import BaseButton from '@/Components/BaseButton.vue';
import BaseButtons from '@/Components/BaseButtons.vue';
import BaseIcon from '@/Components/BaseIcon.vue';
import { Head, Link } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import { mdiArrowLeftBold, mdiCog, mdiTranslate, mdiCheckboxMarkedCircle, mdiAlertCircle, mdiHistory } from '@mdi/js';

const props = defineProps({
  levels: Array,
  translations: Array,
  logs: Array,
  message: String
});

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);
  return date.toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const selectedLevelId = ref(null);
const loading = ref(false);

const criteriaForm = ref({
  criterio1: '',
  criterio2: '',
  criterio3: '',
  criterio4: '',
  criterio5: '',
  criterio6: ''
});



const loadTranslationsForLevel = (levelId) => {
  // Clear inputs
  criteriaForm.value.criterio1 = '';
  criteriaForm.value.criterio2 = '';
  criteriaForm.value.criterio3 = '';
  criteriaForm.value.criterio4 = '';
  criteriaForm.value.criterio5 = '';
  criteriaForm.value.criterio6 = '';

  if (!levelId) return;

  // Filter translations for the selected level
  const levelTrans = props.translations.filter(t => t.level == levelId);

  levelTrans.forEach(trans => {
    if (trans.transkey in criteriaForm.value) {
      criteriaForm.value[trans.transkey] = trans.transvalue;
    }
  });
};

watch(selectedLevelId, (newVal) => {
  loadTranslationsForLevel(newVal);
});

watch(() => props.translations, () => {
  loadTranslationsForLevel(selectedLevelId.value);
}, { deep: true });

onMounted(() => {
  if (props.levels && props.levels.length > 0) {
    selectedLevelId.value = props.levels[0].id;
  }
});

const submit = () => {
  loading.value = true;
  Inertia.post(route('config-criteriosevaluacion-guardar'), {
    id_level: selectedLevelId.value,
    criterios: criteriaForm.value
  }, {
    onFinish: () => {
      loading.value = false;
    }
  });
};
</script>

<template>
  <Head title="Criterios de Evaluación" />

  <BreezeAuthenticatedLayout>
    <template #header>
      <PremiumPageHeader title="Criterios de Evaluación" subtitle="Configura y traduce los nombres de los 6 criterios de calificación por nivel académico.">
        <template #buttons>
          <Link :href="route('config')" class="btn-small btn-blue btn-icon flex items-center gap-2">
            <span class="hidden lg:inline-flex">Volver a Configuración</span>
            <BaseIcon :path="mdiArrowLeftBold" class="text-white" />
          </Link>
        </template>
      </PremiumPageHeader>
    </template>

    <div class="max-w-4xl mx-auto py-6 px-4">
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-2xl shadow-xl p-6">
        
        <!-- Action Notification Messages -->
        <div v-if="$page.props.flash?.message || message" class="mb-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 flex items-center gap-3">
          <BaseIcon :path="mdiCheckboxMarkedCircle" class="w-5 h-5 shrink-0" />
          <span class="text-sm font-medium">{{ $page.props.flash?.message || message }}</span>
        </div>

        <!-- Announcement notice section -->
        <div class="mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 border border-amber-100 dark:border-amber-900/30 flex items-start gap-3 shadow-sm">
          <BaseIcon :path="mdiAlertCircle" class="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
          <div class="text-sm">
            <span class="font-bold block mb-0.5">Nota Importante:</span>
            Si se deja en blanco el nombre de algún criterio, este se considerará desactivado y **no se mostrará** en las calificaciones ni en los reportes de ese nivel académico.
          </div>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          
          <!-- Select Academic Level -->
          <div class="space-y-2">
            <label class="block text-sm font-bold text-slate-700 dark:text-slate-300">
              Seleccionar Nivel / Grado Académico
            </label>
            <div class="relative">
              <select 
                v-model="selectedLevelId" 
                class="w-full bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm font-medium transition-colors"
                required
              >
                <option v-for="lvl in levels" :key="lvl.id" :value="lvl.id">
                  {{ lvl.parent_label }} / {{ lvl.label }}
                </option>
              </select>
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500">
              Los criterios definidos se aplicarán a todas las calificaciones y reportes de este nivel específico.
            </p>
          </div>

          <div class="border-t border-slate-200 dark:border-slate-800 my-6"></div>

          <!-- Dynamic Criteria Config Grid -->
          <div>
            <h3 class="text-base font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
              <BaseIcon :path="mdiTranslate" class="text-indigo-500 w-5 h-5" />
              Traducción y Personalización de Criterios
            </h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Criterio 1 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 1 (Ejemplo: Asistencia)
                </label>
                <input 
                  v-model="criteriaForm.criterio1" 
                  type="text" 
                  name="criterio1"
                  id="criterio1"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>

              <!-- Criterio 2 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 2 (Ejemplo: Participación)
                </label>
                <input 
                  v-model="criteriaForm.criterio2" 
                  type="text" 
                  name="criterio2"
                  id="criterio2"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>

              <!-- Criterio 3 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 3 (Ejemplo: Tarea)
                </label>
                <input 
                  v-model="criteriaForm.criterio3" 
                  type="text" 
                  name="criterio3"
                  id="criterio3"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>

              <!-- Criterio 4 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 4 (Ejemplo: Conducta)
                </label>
                <input 
                  v-model="criteriaForm.criterio4" 
                  type="text" 
                  name="criterio4"
                  id="criterio4"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>

              <!-- Criterio 5 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 5
                </label>
                <input 
                  v-model="criteriaForm.criterio5" 
                  type="text" 
                  name="criterio5"
                  id="criterio5"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>

              <!-- Criterio 6 -->
              <div class="space-y-1">
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Criterio 6
                </label>
                <input 
                  v-model="criteriaForm.criterio6" 
                  type="text" 
                  name="criterio6"
                  id="criterio6"
                  autocomplete="on"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 dark:text-slate-200 text-sm transition-all"
                />
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div class="flex justify-end pt-4">
            <BaseButtons>
              <BaseButton 
                type="submit" 
                color="info" 
                label="Guardar Configuración" 
                :disabled="loading"
              />
            </BaseButtons>
          </div>
        </form>
      </div>

      <!-- Audit Logs Section -->
      <div class="mt-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-2xl shadow-xl p-6">
        <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <BaseIcon :path="mdiHistory" class="text-indigo-500" />
          Historial de Cambios
        </h3>

        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-slate-600 dark:text-slate-400">
            <thead class="bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 uppercase text-xs font-semibold">
              <tr>
                <th class="p-3">Fecha y Hora</th>
                <th class="p-3">Usuario</th>
                <th class="p-3">Nivel / Grado</th>
                <th class="p-3">Año</th>
                <th class="p-3">Criterios Configurables (1 al 6)</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-150 dark:divide-slate-800">
              <tr v-for="log in logs" :key="log.id" class="hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-colors">
                <td class="p-3 whitespace-nowrap text-xs text-slate-500 dark:text-slate-555">
                  {{ formatDateTime(log.created_at) }}
                </td>
                <td class="p-3 whitespace-nowrap font-medium text-slate-700 dark:text-slate-300">
                  {{ log.user_name || 'Desconocido' }}
                </td>
                <td class="p-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                  {{ log.parent_label }} / {{ log.level_label }}
                </td>
                <td class="p-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                  {{ log.anio }}
                </td>
                <td class="p-3">
                  <div class="flex flex-wrap gap-1.5 max-w-md">
                    <span v-for="i in 6" :key="i" class="text-xs px-2 py-0.5 rounded-md font-medium border"
                      :class="log['criterio' + i]
                        ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/30'
                        : 'bg-slate-50 dark:bg-slate-850/40 text-slate-400 dark:text-slate-600 border-slate-150 dark:border-slate-800 line-through'"
                    >
                      C{{ i }}: {{ log['criterio' + i] || 'Desactivado' }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="!logs || logs.length === 0">
                <td colspan="5" class="p-8 text-center text-slate-400 dark:text-slate-500">
                  No hay registros de cambios disponibles
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </BreezeAuthenticatedLayout>
</template>
